import React, { lazy, Suspense, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import FloatingContactWidget from './components/FloatingContactWidget';
import InstallAppPrompt from './components/InstallAppPrompt';

// ── PAGES — code-split via React.lazy so each route loads on-demand
// (Home is eager-loaded since it's the first paint)
import Home from './pages/Home';
const Explore = lazy(() => import('./pages/Explore'));
const Colleges = lazy(() => import('./pages/Colleges'));
const Counseling = lazy(() => import('./pages/Counseling'));
const Exams = lazy(() => import('./pages/Exams'));
const ExamDetails = lazy(() => import('./pages/ExamDetails'));
const Shortlist = lazy(() => import('./pages/Shortlist'));
const Compare = lazy(() => import('./pages/Compare'));
const Scholarships = lazy(() => import('./pages/Scholarships'));
const Quiz = lazy(() => import('./pages/Quiz'));
const Placements = lazy(() => import('./pages/Placements'));
const Benefits = lazy(() => import('./pages/Benefits'));
const CareerLibrary = lazy(() => import('./pages/CareerLibrary'));
const Profile = lazy(() => import('./pages/Profile'));
const Applications = lazy(() => import('./pages/Applications'));
const GuidancePartner = lazy(() => import('./pages/GuidancePartner'));
const Admission = lazy(() => import('./pages/Admission'));
const EagerMinds = lazy(() => import('./pages/EagerMinds'));
const Prism = lazy(() => import('./pages/Prism'));
const CollegeDetail = lazy(() => import('./pages/CollegeDetail'));
const CareerDetail = lazy(() => import('./pages/CareerDetail'));
const PartnerHandoff = lazy(() => import('./pages/PartnerHandoff'));

import { UserProvider, useUser } from './context/UserContext'
import { ToastProvider } from './context/ToastContext';
import './App.css';
import CompareFAB from './components/CompareFAB';

const Vision = lazy(() => import('./pages/Vision'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/legal/TermsAndConditions'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy'));
const RecruiterDashboard = lazy(() => import('./pages/recruiter/Dashboard'));

const ClaimProfile = lazy(() => import('./pages/college/ClaimProfile'));
const InvestorDashboard = lazy(() => import('./pages/InvestorDashboard'));
const Registration = lazy(() => import('./pages/Registration'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const PartnerDashboard = lazy(() => import('./pages/college/PartnerDashboard'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const CollegeDashboard = lazy(() => import('./pages/college/CollegeDashboard'));
const AppShell = lazy(() => import('./app-shell/AppShell'));
const NotFound = lazy(() => import('./pages/NotFound'));

import Footer from './components/Footer';
import GlobalDisclaimer from './components/GlobalDisclaimer';
import SoftSignupPrompt from './components/SoftSignupPrompt';
import WelcomeNamePrompt from './components/auth/WelcomeNamePrompt';
import RequirePhonePrompt from './components/auth/RequirePhonePrompt';
import { GuestNudgeProvider } from './context/GuestNudgeContext';
import ViewAsBanner from './components/ViewAsBanner';
import RouteSkeleton from './components/RouteSkeleton';
import { Sentry, trackPageview } from './lib/analytics';
import { storeFirstTouchUtms } from './lib/utm';

// Wrap the whole app in a Sentry error boundary (fallback is minimal & branded)
const SentryBoundary = Sentry.ErrorBoundary;

function PageviewTracker() {
  const location = useLocation();
  React.useEffect(() => {
    trackPageview(location.pathname + location.search);
    // First-touch UTM attribution. Cheap, idempotent — only writes
    // localStorage the FIRST time it sees utm_* params, then refreshes
    // last-touch on every navigation.
    storeFirstTouchUtms();
  }, [location]);
  return null;
}

/**
 * Captures `?ref=USERID` on first navigation and stashes it in sessionStorage.
 * UserContext.tryCreditPendingReferral picks it up after first-time signup.
 * Survives the Google OAuth round trip because sessionStorage persists across
 * same-origin redirects.
 */
function ReferralCapture() {
  const location = useLocation();
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const ref = params.get('ref');
      if (ref && /^[0-9a-f-]{8,}$/i.test(ref) && typeof sessionStorage !== 'undefined') {
        // Only set if we don't already have one (first-touch attribution)
        if (!sessionStorage.getItem('eager_pending_referrer')) {
          sessionStorage.setItem('eager_pending_referrer', ref);
        }
      }
    } catch { /* noop */ }
  }, [location.search]);
  return null;
}

/**
 * After Google OAuth, Supabase returns the user to `/?code=xxx`.
 * Once hydration completes (userData becomes truthy) AND we're on the root path,
 * auto-navigate to /profile so the user sees their dashboard immediately.
 */
function PostLoginRedirect() {
  const { userData, isLoading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const redirectedRef = React.useRef(false);

  React.useEffect(() => {
    if (isLoading || redirectedRef.current || !userData) return;
    // Only redirect if user just landed post-OAuth (flag set before signInWithOAuth)
    let flag = null;
    let returnTo = '/profile';
    try {
      flag = sessionStorage.getItem('eager_just_signed_in');
      returnTo = sessionStorage.getItem('eager_return_to') || '/profile';
    } catch { /* noop */ }
    const url = new URL(window.location.href);
    const justOAuth = url.searchParams.has('code') || flag === '1';
    if (justOAuth && location.pathname === '/') {
      redirectedRef.current = true;
      try {
        sessionStorage.removeItem('eager_just_signed_in');
        sessionStorage.removeItem('eager_return_to');
      } catch { /* noop */ }
      navigate(returnTo, { replace: true });
    }
  }, [userData, isLoading, location, navigate]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  // Mobile app-shell routes (/app, /app/bytes, …) render their own chrome —
  // hide the website Navbar/Footer/prompts. Note: /applications, /apply do NOT match.
  const isAppShell = /^\/app(\/|$)/.test(location.pathname);

  // ── Routes where the floating WhatsApp/Call widget should appear ───
  // Include high-intent conversion surfaces: landing pages, /counseling,
  // /contact, /claim-profile. Skip /, /explore, /library, /admin — those
  // are browsing / private and the widget would just be noise.
  const showFloatingContact = useMemo(() => {
    const p = location.pathname || '';
    const landingSlugs = [
      '/gaming', '/fashion', '/interior-design', '/graphic-design',
      '/ux-ui-design', '/animation', '/jewellery-design', '/filmmaking',
    ];
    if (landingSlugs.includes(p)) return true;
    if (p.startsWith('/careers/')) return true;
    if (p === '/counseling') return true;
    if (p.startsWith('/admission/')) return true;
    if (p === '/contact') return true;
    if (p === '/claim-profile') return true;
    return false;
  }, [location.pathname]);

  return (
    <React.Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#050810]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
          <div className="text-indigo-400 font-mono text-sm animate-pulse">Initializing Eager...</div>
        </div>
      </div>
    }>
      <ViewAsBanner />
      {!isAppShell && <Navbar />}
      <PostLoginRedirect />
      <ReferralCapture />
      <ScrollToTop />
      {!isAppShell && <GlobalDisclaimer />}
      {!isAppShell && <SoftSignupPrompt />}
      <WelcomeNamePrompt />
      <RequirePhonePrompt />
      <Suspense fallback={<RouteSkeleton />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/eagerminds" element={<EagerMinds />} />
          <Route path="/eager-minds" element={<EagerMinds />} />
          {false && (
            <Route path="/prism" element={<Prism />} />
          )}
          <Route path="/colleges" element={<Colleges />} />
          <Route path="/colleges/:slug" element={<CollegeDetail />} />

          {/* Vertical Career Landing Pages — canonical + direct aliases.
              These are paid-traffic destinations, separate from /library + /explore. */}
          <Route path="/careers/:slug" element={<CareerDetail />} />
          <Route path="/gaming" element={<CareerDetail slugOverride="gaming" />} />
          <Route path="/fashion" element={<CareerDetail slugOverride="fashion" />} />
          <Route path="/interior-design" element={<CareerDetail slugOverride="interior-design" />} />
          <Route path="/graphic-design" element={<CareerDetail slugOverride="graphic-design" />} />
          <Route path="/ux-ui-design" element={<CareerDetail slugOverride="ux-ui-design" />} />
          <Route path="/animation" element={<CareerDetail slugOverride="animation" />} />
          <Route path="/jewellery-design" element={<CareerDetail slugOverride="jewellery-design" />} />
          <Route path="/filmmaking" element={<CareerDetail slugOverride="filmmaking" />} />
          <Route path="/fine-arts" element={<CareerDetail slugOverride="fine-arts" />} />

          {/* Partner college handoff — Eager-branded referral page that redirects
              to the partner's UTM tracking URL (Hamstech, NIFT, Pearl, etc.) */}
          <Route path="/apply/:slug" element={<PartnerHandoff />} />

          {/* Benefits Group */}
          <Route path="/benefits" element={<Benefits />} />
          <Route path="/benefits/exams" element={<Exams />} />
          <Route path="/benefits/exams/:id" element={<ExamDetails />} />
          <Route path="/benefits/placements" element={<Placements />} />
          <Route path="/benefits/scholarships" element={<Scholarships />} />

          <Route path="/counseling" element={<Counseling />} />
          <Route path="/admission" element={<Admission />} />
          <Route path="/shortlist" element={<Shortlist />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/library" element={<CareerLibrary />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/applications" element={<Applications />} />
          <Route path="/register" element={<Registration />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ForgotPassword />} />
          {/* /about and /vision both render the new Vision page.
              /about kept as an alias so existing footer links and any
              backlinks/SEO indexed entries don't 404. */}
          <Route path="/about" element={<Vision />} />
          <Route path="/vision" element={<Vision />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/partners" element={<GuidancePartner />} />
          <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          <Route path="/partners/dashboard" element={<PartnerDashboard />} />

          <Route path="/room/dashboard" element={<PartnerDashboard />} />
          <Route path="/college/dashboard" element={<CollegeDashboard />} />

          {/* Legal Trust Routes */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/refunds" element={<RefundPolicy />} />

          {/* Fallback redirects for legacy links if any */}
          <Route path="/exams" element={<Exams />} />
          <Route path="/placements" element={<Placements />} />
          <Route path="/scholarships" element={<Scholarships />} />

          {/* B2B / Internal Routes (Hidden from Navbar) */}
          <Route path="/recruiter" element={<RecruiterDashboard />} />
          <Route path="/claim-profile" element={<ClaimProfile />} />
          <Route path="/investor" element={<InvestorDashboard />} />
          <Route path="/investors" element={<InvestorDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />

          {/* Mobile app shell (installed PWA experience) — Bytes / Spark / Profile */}
          <Route path="/app" element={<AppShell />} />
          <Route path="/app/:tab" element={<AppShell />} />

          {/* 404 — must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!isHomePage && !isAppShell && <Footer />}
      {showFloatingContact && <FloatingContactWidget enabled />}
      {!isAppShell && <InstallAppPrompt />}
      {!isAppShell && <CompareFAB />}
    </React.Suspense>
  );
}

function App() {
  return (
    <SentryBoundary
      fallback={({ resetError }) => {
        // Preserve the current path so a transient crash on /admin (etc.)
        // doesn't yank the user back to the home page. They can retry in place.
        const currentPath = (typeof window !== 'undefined' && window.location?.pathname) || '/';
        return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white p-6 text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Something broke on our side</h1>
          <p className="text-slate-400 mb-6 max-w-md">Our team has been notified. Try again — most issues clear on a reload.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => { resetError(); window.location.reload(); }}
              data-testid="error-boundary-retry"
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-colors"
            >
              Try Again
            </button>
            {currentPath !== '/' && (
              <button
                onClick={() => { resetError(); window.location.href = '/'; }}
                data-testid="error-boundary-go-home"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold rounded-xl transition-colors"
              >
                Back to Home
              </button>
            )}
          </div>
        </div>
        );
      }}
    >
      <ToastProvider>
        <UserProvider>
          <Router>
            <GuestNudgeProvider>
              <PageviewTracker />
              <AppContent />
            </GuestNudgeProvider>
          </Router>
        </UserProvider>
      </ToastProvider>
    </SentryBoundary>
  );
}

export default App;
